import { useMemo, useState } from 'react';
import useRecentHighlightsQuery from '../../../queries/useRecentHighlightsQuery';
import { theme } from 'config/theme';
import BuildingLineIcon from 'remixicon-react/BuildingLineIcon';
import Chat3LineIcon from 'remixicon-react/Chat3LineIcon';
import GroupLineIcon from 'remixicon-react/GroupLineIcon';
import HandCoinLineIcon from 'remixicon-react/HandCoinLineIcon';
import SearchLineIcon from 'remixicon-react/SearchLineIcon';
import { Box, Grid } from '@mui/material';
import HandleOperation from 'components/utils/HandleOperation';
import CircularHighlight from './CircularHighlight';
import UserLineIcon from 'remixicon-react/UserLineIcon';
import ChipHighlight from './ChipHighlight';
import { ReactComponent as TrendingIcon } from 'assets/icons/trending.svg';
import TopContributingHighlight from './TopContributingHighlight';
import RecentHighlightsSkelton from './RecentHighlightsSkelton';
import { PROJECT } from 'constants/routes';
import ViewUsersModal from '../../ViewUsersModal';
import ViewProjectsModal from '../../ViewProjectsModal';
import ViewPartnersModal from '../../ViewPartnersModal';
import ViewCommentsModal from '../../ViewCommentsModal';
import { useBuildWiseModules } from '@buildwise/libs/helpers';

function RecentHighlights() {
  const { recentActivity, isLoading: isRecentActivityLoading } = useRecentHighlightsQuery({
    shouldFetch: false,
  });
  const [openUsersModal, setOpenUsersModal] = useState(false);
  const [openProjectsModal, setOpenProjectsModal] = useState(false);
  const [openPartnersModal, setOpenPartnersModal] = useState(false);
  const [openCommentsModal, setOpenCommentsModal] = useState(false);
  const { singularProjectsModuleName, pluralProjectsModuleName, pluralPartnersModuleName } =
    useBuildWiseModules();

  // const listData: RenderListProps['list'] = useMemo(
  //   () => [
  //     {
  //       icon: <GroupLineIcon size={22} color={theme.palette.custom.yellowishBrown} />,
  //       message: <FormatUsersActivity users={recentActivity?.users || []} />,
  //       hide: !recentActivity?.users || recentActivity.users.length === 0,
  //     },
  //     {
  //       icon: <BuildingLineIcon size={22} color={theme.palette.custom.yellowishBrown} />,
  //       message: <FormatProjectsActivity projects={recentActivity?.projects || []} />,
  //       hide: !recentActivity?.projects || recentActivity.projects.length === 0,
  //     },
  //     {
  //       icon: <HandCoinLineIcon size={22} color={theme.palette.custom.yellowishBrown} />,
  //       message: <FormatPartnersActivity partners={recentActivity?.partners || []} />,
  //       hide: !recentActivity?.partners || recentActivity.partners.length === 0,
  //     },
  //     // {
  //     //   icon: <FileLineIcon size={22} color={theme.palette.custom.yellowishBrown} />,
  //     //   message: <FormatDocumentsActivity documents={recentActivity?.documents || []} />,
  //     //   hide: !recentActivity?.documents || recentActivity.documents.length === 0,
  //     // },
  //     {
  //       icon: <Chat3LineIcon size={22} color={theme.palette.custom.yellowishBrown} />,
  //       message: <FormatCommentsActivity comments={recentActivity?.comments || []} />,
  //       hide: !recentActivity?.comments || recentActivity.comments.length === 0,
  //     },
  //     {
  //       icon: <SearchLineIcon size={22} color={theme.palette.custom.yellowishBrown} />,
  //       message: (
  //         <FormatPopularSearchKeywords searchKeywords={recentActivity?.searchKeywords || []} />
  //       ),
  //       hide: !recentActivity?.searchKeywords || recentActivity.searchKeywords.length === 0,
  //     },
  //   ],
  //   [recentActivity],
  // );

  const { circularHighlights, chipHighlights, topContributingHighlights } = useMemo(() => {
    let circularHighlights = [
      {
        label: pluralProjectsModuleName,
        icon: <BuildingLineIcon size={20} color={theme.palette.custom.tuna} />,
        message: 'added',
        count: recentActivity?.projects?.length || 0,
        hide: !recentActivity?.projects || recentActivity.projects.length === 0,
        onClick: () => {
          setOpenProjectsModal(true);
        },
      },
      {
        label: 'Members',
        icon: <UserLineIcon size={20} color={theme.palette.custom.tuna} />,
        message: 'joined',
        count: recentActivity?.users?.length || 0,
        hide: !recentActivity?.users || recentActivity.users.length === 0,
        onClick: () => {
          setOpenUsersModal(true);
        },
      },
      {
        label: 'Comments',
        icon: <Chat3LineIcon size={20} color={theme.palette.custom.tuna} />,
        message: 'added',
        count: recentActivity?.comments?.length || 0,
        hide: !recentActivity?.comments || recentActivity.comments.length === 0,
        onClick: () => {
          setOpenCommentsModal(true);
        },
      },
      {
        label: pluralPartnersModuleName,
        icon: <HandCoinLineIcon size={20} color={theme.palette.custom.tuna} />,
        message: 'added',
        count: recentActivity?.partners?.length || 0,
        hide: !recentActivity?.partners || recentActivity.partners.length === 0,
        onClick: () => {
          setOpenPartnersModal(true);
        },
      },
    ];
    let chipHighlights = [
      {
        label: 'Trending Topics',
        icon: <TrendingIcon width={24} height={24} />,
        isChip: true,
        searchKeywords: recentActivity?.trendingTopics || [],
        hide: !recentActivity?.trendingTopics || recentActivity.trendingTopics.length === 0,
      },
      {
        label: 'Popular Searches',
        icon: <SearchLineIcon size={20} color={theme.palette.custom.yellowishBrown} />,
        isChip: true,
        searchKeywords: recentActivity?.searchKeywords || [],
        hide: !recentActivity?.searchKeywords || recentActivity.searchKeywords.length === 0,
      },
    ];
    let topContributingHighlights = [
      {
        label: 'Top Contributing Team',
        icon: <GroupLineIcon size={22} color={theme.palette.custom.navy} />,
        name: recentActivity?.topContributingTeams?.[0] || '',
        hide:
          !recentActivity?.topContributingTeams ||
          recentActivity?.topContributingTeams?.length === 0,
      },
      {
        label: `Top Contributing ${singularProjectsModuleName}`,
        icon: (
          <Box
            sx={{
              width: 30,
              height: 30,
              backgroundColor: theme.palette.custom.lightBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
            }}
          >
            <BuildingLineIcon size={22} color={theme.palette.custom.tuna} />
          </Box>
        ),
        name: recentActivity?.trendingProjectsInArticles?.[0]?.name || '',
        hide:
          !recentActivity?.trendingProjectsInArticles ||
          recentActivity?.trendingProjectsInArticles?.length === 0,
        url: recentActivity?.trendingProjectsInArticles?.[0]?.id
          ? PROJECT.replace(':projectId', recentActivity?.trendingProjectsInArticles?.[0]?.id)
          : '',
      },
    ];
    return { circularHighlights, chipHighlights, topContributingHighlights };
  }, [recentActivity, singularProjectsModuleName]);

  return (
    <>
      <HandleOperation
        isLoading={isRecentActivityLoading}
        data={circularHighlights}
        skeltonCount={1}
        skelton={<RecentHighlightsSkelton />}
      >
        <Box py={1} px={1}>
          {/* <RenderList list={listData} /> */}
          <Grid container spacing={2} pb={1}>
            {circularHighlights?.map(
              (highlight) =>
                !highlight.hide && (
                  <Grid
                    item
                    md={6}
                    sm={12}
                    mt={2}
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    key={highlight.label}
                  >
                    <CircularHighlight {...highlight} />
                  </Grid>
                ),
            )}
          </Grid>

          {chipHighlights?.map(
            (highlight) =>
              !highlight.hide && <ChipHighlight {...highlight} key={highlight.label} />,
          )}

          {topContributingHighlights?.map(
            (highlight) =>
              !highlight.hide && <TopContributingHighlight {...highlight} key={highlight.label} />,
          )}
        </Box>
      </HandleOperation>
      {openUsersModal && !!recentActivity?.users?.length && (
        <ViewUsersModal
          open={openUsersModal}
          setOpen={setOpenUsersModal}
          users={recentActivity?.users}
        />
      )}
      {openProjectsModal && !!recentActivity?.projects?.length && (
        <ViewProjectsModal
          open={openProjectsModal}
          setOpen={setOpenProjectsModal}
          projects={recentActivity?.projects}
        />
      )}
      {openPartnersModal && !!recentActivity?.partners?.length && (
        <ViewPartnersModal
          open={openPartnersModal}
          setOpen={setOpenPartnersModal}
          partners={recentActivity.partners}
        />
      )}
      {openCommentsModal && !!recentActivity?.comments?.length && (
        <ViewCommentsModal
          open={openCommentsModal}
          setOpen={setOpenCommentsModal}
          comments={recentActivity?.comments}
        />
      )}
    </>
  );
}

export default RecentHighlights;
